import { useQuery } from '@tanstack/react-query';
import { fallbackPrices, type PlanPricing } from '@/data/pricing/fallbackPrices';

const PRICING_API_URL =
  'https://seniorthrive-backend-us-471216032729.us-central1.run.app/api/stripe/public/pricing';

interface StripePlan {
  planId: string;
  name: string;
  description: string | null;
  isBasePlan: boolean;
  isAddon: boolean;
  priceCurrency: string | null;
  monthlyPriceAmount: number | null;
  annualPriceAmount: number | null;
  stripeProductId: string | null;
  stripePriceMonthlyId: string | null;
  stripePriceAnnualId: string | null;
}

interface PricingApiResponse {
  plans: StripePlan[];
  version: string;
  generatedAt: string;
}

const nameToKey: Record<string, string> = {
  ThriveMax: 'thrivemax',
  ThriveFamily: 'thrivefamily',
  'ThriveFamily Plus': 'thrivefamily-plus',
  'ThriveCare Add-On': 'thrivecare-addon',
  'ThriveCare Independent': 'thrivecare-independent',
  ThriveStarter: 'thrivestarter',
};

function mapApiToPlans(data: PricingApiResponse): Record<string, PlanPricing> {
  const result: Record<string, PlanPricing> = {};
  for (const plan of data.plans) {
    const key = nameToKey[plan.name] || plan.name.toLowerCase().replace(/\s+/g, '-');
    const monthly = plan.monthlyPriceAmount ?? 0;
    const annual = plan.annualPriceAmount ?? null;
    result[key] = {
      id: key,
      name: plan.name,
      monthlyPrice: monthly,
      annualPrice: annual,
      monthlyEquivalent: annual ? Math.round(annual / 12) : monthly,
      isAddOn: plan.isAddon,
      perUnit: key === 'thrivecare-addon' ? 'caregiver' : undefined,
    };
  }
  return result;
}

async function fetchPricing(): Promise<Record<string, PlanPricing>> {
  const res = await fetch(PRICING_API_URL);
  if (!res.ok) throw new Error(`Pricing API error: ${res.status}`);
  const data: PricingApiResponse = await res.json();
  return mapApiToPlans(data);
}

export function usePricingData() {
  const query = useQuery({
    queryKey: ['stripe-pricing'],
    queryFn: fetchPricing,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: 2,
    placeholderData: fallbackPrices,
  });

  const prices = query.data ?? fallbackPrices;

  const getPlan = (key: string): PlanPricing | undefined => prices[key];

  return {
    prices,
    getPlan,
    isLoading: query.isLoading,
    isError: query.isError,
    isFallback: query.isError || !query.data,
    refetch: query.refetch,
  };
}

export type { PlanPricing, StripePlan, PricingApiResponse };
